import {
  Component, Inject, OnDestroy, OnInit, ViewEncapsulation, Input, Output, EventEmitter, HostListener,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { distinctUntilChanged, map, take, tap, switchMap, filter } from 'rxjs/operators';
import { combineLatest, iif, Observable, Subscription, zip, of } from 'rxjs';
import isEqual from 'lodash-es/isEqual';
import isEmpty from 'lodash-es/isEmpty';

import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { AppFacade } from '@app/app.facade';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { HotelDetailActionService } from '@hotels/hotel-detail/services/hotel-detail-action.service';
import { hotelAmplitudeProductPropertiesMapper } from '@app/modules/core/mappers/hotels-amplitude.mapper';
import { BaseSearchBarFacade } from '@app/modules/shared/base-search-bar/base-search-bar-facade';
import { PackageStep } from '@app/modules/packages/packages-search-bar/constants/package-step';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { totalNights } from '@ux/mappers/guest.mapper';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';

@Component({
  selector: 'app-hotel-detail',
  templateUrl: './hotel-detail.container.html',
  styleUrls: ['./hotel-detail.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class HotelDetailContainer implements OnInit, OnDestroy {

  @Input() hotelDetailParams: ParamMap;
  @Input() headerSticky: boolean = false;
  @Input() source: EVENT_SOURCE | string = null;

  @Output() redirectToSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleAmenities: EventEmitter<void> = new EventEmitter<void>();
  @Output() goToGallery: EventEmitter<void> = new EventEmitter<void>();

  private subscriptions: Subscription[] = [];
  private heigthScrollSticky: number = 385;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private route: ActivatedRoute,
    private mobileDeviceService: MobileDeviceService,
    private hotelDetailActionService: HotelDetailActionService,
    private facade: HotelDetailFacade,
    private appFacade: AppFacade,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private domManipulateService: DomManipulateService,
    private amplitude: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    if (this.isBrowser) {
      this.subscriptions.push(
        this.hotelDetailActionService.listerUrlParamsByHotelDetail(this.route.paramMap, this.route.queryParamMap).pipe(
          distinctUntilChanged((current, preview) => isEqual(current, preview))
        ).subscribe());
    }
  }

  @HostListener('window:scroll', ['$event']) onScrollWindow(event: Event): void {
    const offset = window['pageYOffset'] || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.headerSticky = offset >= this.heigthScrollSticky;
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.domManipulateService.setBodyScroll(true);
      this.subscriptions.push(
        this.appFacade.previousUrl$.pipe(tap(commands => {
          const criteria = (x: Object) => x.toString().indexOf(`/${homeItemDetailRootRoute}/`) === 0;
          if (commands.some(criteria)) {
            const item = commands.find(criteria).toString();
            if (item) this.source = item.slice(item.lastIndexOf('/') + 1);
          }
        })).subscribe()
      );
      this.subscriptions.push(
        combineLatest([
          this.isLoadingHotelDetail$, 
          this.route.queryParamMap.pipe(take(1)), 
          this.isPackageDetail$, 
          this.hotelDetail$,
          this.baseSearchBarFacade.hourType$
        ]).pipe(
          map(([isLoading, queries, isPackageDetail, detail, hourTypes]) => 
            ({ isLoading, queries, isPackageDetail, detail, hourTypes })),
          filter(({ isLoading, detail }) => 
            !isLoading && !isEmpty(detail) && !isEmpty(detail?.Result) && !isEmpty(detail?.Info)),
          switchMap(({ isPackageDetail, detail, queries, hourTypes }) => {
            const searchByHotelOrMicrostay = detail?.Result?.MicroStay ? SearchBy.BYHOURS : SearchBy.HOTELS;
            return zip(
              of({ detail, queries, hourTypes }),
              iif(
                () => !isPackageDetail,
                this.amplitude.searchProperties$(searchByHotelOrMicrostay).pipe(take(1)),
                this.amplitude.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
              )
            )
          }),
          map(([{ queries, detail, hourTypes }, searchProps]) => ({
            eventName: AVO_EVENTS.VIEW_TRAVEL_ITEM_DETAIL,
            eventParams: {
              SOURCE: queries.get(SOURCE_PARAM) || this.source,
              HAS_USER_RATINGS: !isEmpty(detail?.Info?.Ratings)
            },
            searchProps, productProps: hotelAmplitudeProductPropertiesMapper(detail.Result, detail.Info, parseInt(hourTypes?.[0]))
          })),
          switchMap(({ eventName, eventParams, searchProps, productProps }) =>
            this.avoService.getEventTriggerProps$(eventName, eventParams, searchProps, productProps)
          ),
          tap(trigger => {
            this.AvoDispatch.viewTravelItemDetail({
              ...this.avoService.avoBaseProperties(trigger),
              ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
              ...this.avoService.avoProductProps(trigger?.PRODUCT_PROPS),
              source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
              index: trigger?.EVENT_PROPS?.INDEX as number
            });
          })
        ).subscribe()
      );
    }
  }

  ngOnDestroy(): void {
    removeSubscriptions(this.subscriptions);
  }

  get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$; 
  }
  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$; 
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get totalNightsNumber$(): Observable<number> {
    return this.facade.hotelDetail$.pipe(map(hotel => {
      const checkOutDate = hotel?.Result ? hotel.Result.CheckoutDate : '';
      const checkInDate = hotel?.Result ? hotel.Result.CheckinDate : '';
      return totalNights(checkOutDate, checkInDate);
    }));
  }

  get  errors$(): Observable<ErrorHandle> {
    return this.facade.errors$;
  }

  get noDetailResults$(): Observable<boolean> {
    return combineLatest([
      this.isLoadingHotelDetail$,
      this.hotelDetail$]).pipe(
      map(([isLoadingHotelDetail, hotelDetail]) => (!isLoadingHotelDetail && isEmpty(hotelDetail)))
    )
  }

  private get hotelDetail$(): Observable<HotelDetailResponse> | null {
    return this.facade.hotelDetail$.pipe(map(detail => detail.Info || detail.Result ? detail : null));
  }

  private get isPackageDetail$(): Observable<boolean> {
    return this.baseSearchBarFacade.packageStep$.pipe(map(packageStep =>
      !!packageStep && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL));
  }

  private get isLoadingHotelDetail$(): Observable<boolean> {
    return this.facade.isLoadingHotelDetail$;
  }

}
